import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

interface LineChartConfig {
  id: string;
  filepath: string;
  dataString?: string;
  fillColor?: am5.Color;
  lineColor?: am5.Color;
  fillVisible?: boolean;
}

interface LineChartData {
  name: string;
  value: number;
  tooltiptext: string;
}

interface ChartWithId extends am5xy.XYChart {
  id?: string;
  data?: LineChartData[];
}

interface RootWithId extends am5.Root {
  id?: string;
}

declare const linechartarray: LineChartConfig[];

const lineCharts: ChartWithId[] = [];
const lineChartRoots: RootWithId[] = [];

if (typeof linechartarray !== "undefined") {
  for (const x of linechartarray) {
    am5.net.load(x.filepath).then((result) => {
      let data: LineChartData[] = [];

      if (x.filepath.includes(".csv")) {
        if (result.response) {
          const delimiter = result.response.split(";").length === 1 ? "," : ";";
          data = am5.CSVParser.parse(result.response, {
            delimiter,
            useColumnNames: true,
          }) as LineChartData[];
        }
      } else if (x.dataString) {
        const dataArr = x.dataString.split(",");
        for (let i = 0; i < dataArr.length; i += 3) {
          data.push({
            name: dataArr[i],
            value: parseFloat(dataArr[i + 1]),
            tooltiptext: dataArr[i + 2],
          });
        }
      }

      const root = am5.Root.new(x.id) as RootWithId;
      root.setThemes([am5themes_Animated.new(root)]);

      const chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
      })) as ChartWithId;

      chart.zoomOutButton.set("exportable", false);
      chart.set("scrollbarX", am5.Scrollbar.new(root, {
        orientation: "horizontal",
        exportable: false,
      }));

      // Custom screen reader strings
      root.locale["From %1"] = "Anv�nd piltangenterna f�r att zooma...";
      root.locale["To %1"] = "Anv�nd piltangenterna f�r att zooma...";

      const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        categoryField: "name",
        renderer: am5xy.AxisRendererX.new(root, {}),
      }));

      const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {}),
        tooltip: am5.Tooltip.new(root, {}),
      }));

      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        autoTextColor: false,
        labelText: "{tooltiptext}",
      });

      tooltip.get("background")?.setAll({
        fill: am5.color("#fff")
      });


      tooltip.label.setAll({ fill: am5.color(0xffffff) });
      tooltip.get("background")?.setAll({ fill: am5.color("#696969") });

      const series = chart.series.push(am5xy.LineSeries.new(root, {
        name: "Series",
        xAxis,
        yAxis,
        valueYField: "value",
        categoryXField: "name",
        fill: x.fillColor,
        stroke: x.lineColor,
        tooltip,
        locationX: 0,
      }));

      series.fills.template.setAll({
        fillOpacity: 0.5,
        visible: x.fillVisible,
      });

      series.data.processor = am5.DataProcessor.new(root, {
        numericFields: ["value"],
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);

      // Bullet points (value markers)
      series.bullets.push(() => am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          strokeWidth: 3,
          stroke: series.get("stroke"),
          radius: 5,
          fill: root.interfaceColors.get("background"),
          focusable: true,
          ariaLabel: "{name}. {value}. {tooltiptext}.",
        }),
        locationX: 0,
      }));

      chart.set("cursor", am5xy.XYCursor.new(root, {}));
      chart.appear(1000, 100);
      series.appear();

      // Save for export
      chart.id = x.id;
      chart.data = data;
      root.id = x.id;

      lineCharts.push(chart);
      lineChartRoots.push(root);
    })
  }
}



export function exportLineChart(format: "png" | "jpg" | "pdf" | "xlsx" | "csv" | "json" | "pdfdata" , id: string) {
  const chart = lineCharts.find(c => c.id === id);
  const root = lineChartRoots.find(r => r.id === id);
  if (!chart || !root) return;

  const exporting = am5plugins_exporting.Exporting.new(root, {
    dataSource: chart.data!,
    filePrefix: "Linjediagram",
  });

  exporting.download(format);
}
